import { computed, onMounted, ref } from 'vue';
import {
  COMMISSION_GROUPS,
  commissionFormFromTransaction,
  emptyInstallment,
  moneyCents,
  payloadFromCommissionForm,
} from './salesCommissionForm';
import { payerOptionsFromParticipants } from './salesCommissionParticipantForm';
import {
  loadSalesCommission,
  saveSalesCommissionDraft,
} from './salesCommissionRequests';
import { salesCommissionMutations } from './salesCommissionState';
import {
  hasCommissionErrors,
  salesCommissionSectionErrors,
} from './salesCommissionValidation';
import { salesCommissionProgressState } from './salesCommissionProgress';
export function useSalesCommissionForm({ id, t, notify }) {
  const transaction = ref(null);
  const catalog = ref({});
  const form = ref(commissionFormFromTransaction());
  const activeSection = ref('company');
  const errors = ref({});
  const loading = ref(false);
  const saving = ref(false);
  const requestError = ref('');
  const forbidden = ref(false);
  const sections = [
    'company',
    'installments',
    ...COMMISSION_GROUPS.map(group => group.key),
  ];
  const currentIndex = computed(() => sections.indexOf(activeSection.value));
  const canGoBack = computed(() => currentIndex.value > 0);
  const canGoNext = computed(() => currentIndex.value < sections.length - 1);
  const payerOptions = computed(() =>
    payerOptionsFromParticipants(transaction.value?.participants)
  );
  const completion = computed(() =>
    salesCommissionProgressState({ form: form.value, sections, t })
  );
  const availableSections = computed(() => completion.value.availableSections);
  const completedSections = computed(() => completion.value.completedSections);
  const progress = computed(() => completion.value.percent);
  const mutations = salesCommissionMutations({ form, catalog, errors });
  const ensureInstallment = () => {
    if (
      activeSection.value !== 'installments' ||
      form.value.installments.length
    )
      return;
    form.value = {
      ...form.value,
      installments: [emptyInstallment(moneyCents(form.value.net_amount))],
    };
  };
  const load = async () => {
    if (!id.value) return;
    loading.value = true;
    requestError.value = '';
    forbidden.value = false;
    try {
      const result = await loadSalesCommission(id.value);
      transaction.value = result.transaction;
      catalog.value = result.catalog;
      form.value = commissionFormFromTransaction(transaction.value);
      activeSection.value = completion.value.firstIncomplete || sections[0];
      ensureInstallment();
    } catch (error) {
      if ([401, 403].includes(error?.response?.status)) {
        forbidden.value = true;
        return;
      }
      requestError.value =
        error?.response?.data?.message || t('SALES.COMMISSION.ERRORS.LOAD');
      notify(requestError.value);
    } finally {
      loading.value = false;
    }
  };
  const save = async () => {
    const nextErrors = salesCommissionSectionErrors({
      form: form.value,
      section: activeSection.value,
      t,
    });
    errors.value = nextErrors;
    if (hasCommissionErrors(nextErrors)) return null;
    saving.value = true;
    try {
      const savedTransaction = await saveSalesCommissionDraft(
        id.value,
        payloadFromCommissionForm(form.value)
      );
      transaction.value = {
        ...transaction.value,
        ...savedTransaction,
        participants:
          savedTransaction.participants ||
          transaction.value?.participants ||
          [],
      };
      notify(t('SALES.COMMISSION.ALERTS.SAVED'));
      return transaction.value;
    } catch (error) {
      notify(
        error?.response?.data?.message || t('SALES.COMMISSION.ERRORS.SAVE')
      );
      return null;
    } finally {
      saving.value = false;
    }
  };
  const goTo = section => {
    if (!availableSections.value.includes(section)) return;
    activeSection.value = section;
    errors.value = {};
  };
  const previous = () => goTo(sections[currentIndex.value - 1]);
  const next = async () => {
    if (!(await save())) return;
    goTo(sections[currentIndex.value + 1]);
    ensureInstallment();
  };
  onMounted(load);
  return {
    activeSection,
    availableSections,
    canGoBack,
    canGoNext,
    catalog,
    completedSections,
    ...mutations,
    errors,
    forbidden,
    form,
    goTo,
    load,
    loading,
    next,
    payerOptions,
    previous,
    progress,
    requestError,
    save,
    saving,
    sections,
    transaction,
  };
}
